import React from "react";
import { useFeatureFlag } from "@/shared/hooks/useFeatureFlag";
import { FeatureFlagKey } from "@/types/feature-flags";

interface FeatureGateProps {
  flagKey: FeatureFlagKey;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showFallback?: boolean;
}

/**
 * FeatureGate component that conditionally renders children based on feature flag
 *
 * @param flagKey - The key of the feature flag to check
 * @param children - Content to render when flag is enabled
 * @param fallback - Content to render when flag is disabled (optional)
 * @param showFallback - Whether to show fallback content when flag is disabled (default: false)
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  flagKey,
  children,
  fallback = null,
  showFallback = false,
}) => {
  const isEnabled = useFeatureFlag(flagKey);

  if (isEnabled) {
    return <>{children}</>;
  }

  if (showFallback && fallback) {
    return <>{fallback}</>;
  }

  return null;
};

export default FeatureGate;
